import actions from './actions'
import types from './constants'
import services from '../../services/progress'
import { generateProgressReport } from '../../util/generators/generateProgressReport';
import { generateProgressReportTrendline } from '../../util/generators/generateTrendline';
import { getTotalSeconds } from '../../util/getTotalSeconds';
import { shouldFilterStoryFromReport } from '../../../../services/EarlyReaderService';
import { fetchStudentTrends } from '../../util/generators/generateClassProgressReport';
import { LOCALE_OPTIONS } from '../../../../services/badging/constants';
import { localesUnderscoreMap } from '../../../../constants/constants';
import { fetchScaledCutlines as fetchScaledCutlinesAPI } from '../../../../services/query/benchmarkReportAPI';
import { getSchoolYearWithFallbacks } from '../../../../utilities/getSchoolYearWithFallbacks';
import { get } from 'lodash';
import store from '../../../../store';
import { getScaleFromMetricId } from '../../util/metricScaleUtils';

// Helper function to wait for Redux state condition
const waitForState = (selector, timeout = 5000) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    
    // Check if condition is already met
    if (selector(store.getState())) {
      return resolve(store.getState());
    }
    
    // Set up subscription
    const unsubscribe = store.subscribe(() => {
      if (selector(store.getState())) {
        unsubscribe();
        resolve(store.getState());
      } else if (Date.now() - startTime > timeout) {
        unsubscribe();
        reject(new Error('Timeout waiting for state condition'));
      }
    });
  });
};

const progressSelect = (selection) => (dispatch) => {
  dispatch(actions[types.PROGRESS_SELECT](selection))
}

const fetchReport = (student, metric, scale, equating, locale, additionalProps) => async (dispatch) => {
  dispatch(actions[types.PROGRESS_PENDING]())
  try {
    // Get scaled cutlines from Redux state
    let scaledCutlines = null;
    const state = store.getState();
    const districtId = get(state, 'reports.roster.data.district[0].id');
    const schoolId = get(state, 'reports.roster.data.school[0].id');

    if (districtId && schoolId && student?.grade !== undefined) {
      try {
        // Wait for assessment calendar periods to be available
        await waitForState(state => {
          const calendar = state.calendar.assessmentCalendar;
          return calendar?.periods?.length > 0;
        });
        
        const assessmentCalendar = store.getState().calendar.assessmentCalendar;
        const scale = getScaleFromMetricId(metric);
        if (assessmentCalendar?.periods?.length > 0) {
          const periodIds = assessmentCalendar.periods.map(period => period.periodIdentifier);
          const params = {
            districtId,
            schoolId,
            grade: student.grade,
            scale,
            periodId: periodIds
          };
          
          const fetchedCutlines = await fetchScaledCutlinesAPI(params);
          
          // Ensure we have a valid array
          if (Array.isArray(fetchedCutlines)) {
            scaledCutlines = fetchedCutlines;
            console.log("✅ Successfully set scaledCutlines");
          } else {
            console.warn("❌ API returned non-array cutlines:", typeof fetchedCutlines, fetchedCutlines);
            scaledCutlines = null;
          }
        } else {
          console.warn("❌ No assessment calendar periods available after wait");
        }
      } catch (error) {
        console.warn("❌ Failed to get assessment calendar periods or fetch scaled cutlines:", error);
        // Continue without cutlines rather than failing completely
      }
    } else {
      console.warn("❌ Missing required data for cutlines");
    }

    const [report, activities] = await services.getReport(student, metric, scale, equating, locale, additionalProps, scaledCutlines);
    const studentObjectForTrend = [{id: student.value, grade: student.grade}];
    const language = LOCALE_OPTIONS.find(i => i.value === localesUnderscoreMap[locale])?.label;
    const trends = await fetchStudentTrends(studentObjectForTrend, language);
    report.trend = trends?.[0]?.trend; // it returns only the one student we requested
    dispatch(actions[types.PROGRESS_COMPLETE](report, activities));
  }catch(e){
    console.error("Error fetching report or student trends:", e);
    dispatch(actions[types.PROGRESS_ERROR](e.message));
  }
}

// TODO: looks like scaleReport is not used anywhere, clean up here and in index.js
const scaleReport = (activities, metric, scale, benchmark, student, customBenchmarks,locale, selectedStatePercentileCuts) => async (dispatch) => {
  if(activities && activities.length > 0) {
    let report = await generateProgressReport(activities, metric, scale, locale);
    report = await accountForTrendline(report, scale, student, locale);
    dispatch(actions[types.PROGRESS_UPDATED](report, activities));
  }
};

const fetchActivities = (studentId, equating, locale, scale) => async (dispatch) => {
  dispatch(actions[types.PROGRESS_PENDING]());
  try {
    const activities = await services.fetchActivities(studentId, equating, locale, scale);
    dispatch(actions[types.PROGRESS_COMPLETE](null, activities));
    return activities;
  } catch (error) {
    console.error("Error fetching activities:", error);
    dispatch(actions[types.PROGRESS_ERROR](error.message));
    throw error;
  }
};

export const fetchScaledCutlines = (districtId, schoolId, grade, scale, periodIds) => async (dispatch) => {
  dispatch(actions[types.PROGRESS_PENDING]());
  try {
    const cutlines = await fetchScaledCutlinesAPI({
      districtId,
      schoolId,
      grade,
      scale,
      periodId: periodIds,
    });
    dispatch(actions[types.PROGRESS_CUTLINES_COMPLETE](cutlines));
  } catch (error) {
    dispatch(actions[types.PROGRESS_ERROR](error));
    throw error;
  }
};

// Shift goodCutoff by a constant, and change yMax if warrented once the trendline is generated
export async function accountForTrendline(report, scale, student, locale) {
  if(report && report.data && report.data.length > 0) {
    // filter out early reader activities and short audio activities
    const reportDataFiltered = report.data.filter((data) => {

      const totalSeconds = getTotalSeconds(data.timeRead);

      // return false if short audio activity
      if (totalSeconds < 30) return false;

      // return false if early reader is true
      if (data.storyTags && shouldFilterStoryFromReport(data, scale)) return false;

      return true;
    })
    if (reportDataFiltered[0]) {
      // Get school year end date using fallback utility
      const state = store.getState();
      const assessmentCalendar = state.calendar?.assessmentCalendar;
      const schoolYearCalendar = state.calendar?.schoolYearCalendar;
      
      const schoolYearResult = getSchoolYearWithFallbacks(assessmentCalendar, schoolYearCalendar);
      const schoolYearEndDate = schoolYearResult.endDate;
      
      const trendlinePromises = [];
      
      // Main report trendline
      trendlinePromises.push(
        generateProgressReportTrendline(reportDataFiltered, scale, student && student.grade, locale, schoolYearEndDate)
          .then(trendline => { report.trendline = trendline; })
      );
      
      // Subscore trendlines
      if (report.subscores) {
        report.subscores.forEach((subscore, index) => {
          trendlinePromises.push(
            generateProgressReportTrendline(subscore.data, subscore.scale, student && student.grade, locale, schoolYearEndDate)
              .then(trendline => { report.subscores[index].trendline = trendline; })
          );
        });
      }
      
      // Wait for all trendlines to complete
      await Promise.all(trendlinePromises);
    }
    if(report && report.trendline && report.trendline.length > 0) {
      let trendMax = report.trendline[report.trendline.length - 1][1] * 1.1;
      if(trendMax > report.yMax) {
        let difference = trendMax - report.yMax;
        report.yMax = trendMax;
        report.greatCutoff = report.greatCutoff.map(cutoff => {
          cutoff[1] = cutoff[1] + difference;
          return cutoff;
        });
      }
    }
  }
  return report;
}

export default {
  progressSelect,
  fetchReport,
  scaleReport,
  fetchActivities,
  fetchScaledCutlines,
}
